<template>
    <PageWrapper>
        <template #headline>
            <fa icon="fa-paint-roller" />
            {{ $t('customize') }} {{ platformName }} Menu
        </template>
        <div
            class="flex h-auto w-full flex-col items-start justify-start space-y-4 rounded-lg bg-gray-200 p-4"
        >
            <div class="flex h-auto w-full items-center justify-between">
                <div class="flex h-auto w-auto items-center justify-start">
                    <p class="text-xl font-bold">Menu Items</p>
                </div>
                <div
                    class="flex h-10 w-auto items-center justify-end space-x-2"
                >
                    <LangSwitch v-model:activeLocale="activeLocale" />
                    <button
                        class="rounded-md bg-blue-500 px-4 py-2 text-sm text-white"
                        @click="handleAdd"
                    >
                        <fa
                            icon="fa-plus"
                            class="mr-1"
                        />
                        Add
                    </button>
                </div>
            </div>
            <MenuItemsTable
                :menu-items="menuItems"
                :active-locale="activeLocale"
                @edit="handleEdit"
                @delete="handleDelete"
                @addChild="handleAddChild"
                @reorder="handleReorder"
            />
        </div>
        <MenuItemFormModal
            v-if="menuItemFormModalOpen"
            :item="menuItemFormModalItem ?? undefined"
            :parent-id="menuItemFormModalParentId ?? undefined"
            :context="menuType ?? undefined"
            @cancel="menuItemFormModalOpen = false"
            @submit="handleSubmit"
        />
        <Modal
            id="delete-menu-item-modal"
            title="delete_menu_item"
            :open="deleteMenuItemModalOpen"
            @cancel="deleteMenuItemModalOpen = false"
            @submit="handleDeleteConfirm"
        >
            <p>are you sure you want to delete this menu item?</p>
            <template #actions>
                <button
                    type="submit"
                    class="btn-sm bg-red-500 text-white hover:bg-red-600"
                >
                    Delete
                </button>
            </template>
        </Modal>
    </PageWrapper>
</template>

<script setup lang="ts">
import { SUPPORTED_LOCALES } from '@builder/defaults'
import { LocaleMap, PlatformMenuItem } from '@builder/types'
import Modal from '@src/components/Modal.vue'
import LangSwitch from '@src/partials/pageBuilder/LangSwitch.vue'
import MenuItemFormModal from '@src/partials/pageBuilder/MenuItemFormModal.vue'
import MenuItemsTable from '@src/partials/pageBuilder/MenuItemsTable.vue'
import PageWrapper from '@src/partials/pages/PageWrapper.vue'
import {
    getPlatformMenu,
    updatePlatformMenu,
} from '@src/utils/api/platformMenus'
import { useToastStore } from '@src/utils/stores/toast'
import { onMounted, ref } from 'vue'
import { useRoute } from 'vue-router'

const route = useRoute()
const toast = useToastStore()
const menuId = route.params.menuId as string
const platformName = route.params.slug as string
const menuItems = ref<PlatformMenuItem[]>([])
const menuType = ref<'header' | 'footer' | null>(null)
const activeLocale = ref<keyof LocaleMap>(SUPPORTED_LOCALES[0])
const menuItemFormModalOpen = ref(false)
const menuItemFormModalItem = ref<PlatformMenuItem | null>(null)
const menuItemFormModalParentId = ref<string | null>(null)
const deleteMenuItemModalOpen = ref(false)
const deletableMenuItemId = ref<string | null>(null)

const handleEdit = (item: PlatformMenuItem) => {
    menuItemFormModalOpen.value = true
    menuItemFormModalItem.value = item
}

const handleDelete = (item: PlatformMenuItem) => {
    deletableMenuItemId.value = item.id
    deleteMenuItemModalOpen.value = true
}

const handleDeleteConfirm = async () => {
    const id = deletableMenuItemId.value
    menuItems.value = menuItems.value
        .filter((i) => i.id !== id)
        .map((i) => {
            if (i.children && i.children.length > 0) {
                return { ...i, children: i.children.filter((c) => c.id !== id) }
            }
            return i
        })
    deleteMenuItemModalOpen.value = false
    deletableMenuItemId.value = null
    const res = await updatePlatformMenu(platformName, menuId, menuItems.value)
    if (res.status === 200) {
        menuItems.value = res.data.items
        toast.success('Menu updated')
    }
}

const handleSubmit = async (
    item: PlatformMenuItem,
    parentId: string | null
) => {
    const topIndex = menuItems.value.findIndex((i) => i.id === item.id)
    if (topIndex !== -1) {
        menuItems.value[topIndex] = item
    } else {
        const parentWithChild = menuItems.value.find((i) =>
            i.children?.some((c) => c.id === item.id)
        )
        if (parentWithChild) {
            parentWithChild.children = parentWithChild.children!.map((c) =>
                c.id === item.id ? item : c
            )
        } else if (parentId) {
            const parent = menuItems.value.find((i) => i.id === parentId)
            if (parent) {
                parent.children = [...(parent.children || []), item]
            }
        } else {
            menuItems.value.push(item)
        }
    }

    menuItemFormModalOpen.value = false
    const res = await updatePlatformMenu(platformName, menuId, menuItems.value)
    if (res.status === 200) {
        menuItems.value = res.data.items
        toast.success('Menu updated')
    }
}

const handleAdd = () => {
    menuItemFormModalOpen.value = true
    menuItemFormModalItem.value = null
    menuItemFormModalParentId.value = null
}

const handleReorder = async (items: PlatformMenuItem[]) => {
    menuItems.value = items
    const res = await updatePlatformMenu(platformName, menuId, menuItems.value)
    if (res.status === 200) {
        menuItems.value = res.data.items
        toast.success('Menu updated')
    }
}

const handleAddChild = (item: PlatformMenuItem) => {
    menuItemFormModalOpen.value = true
    menuItemFormModalItem.value = null
    menuItemFormModalParentId.value = item.id
}

onMounted(async () => {
    const res = await getPlatformMenu(platformName, menuId)
    if (res.status === 200) {
        menuItems.value = res.data.items
        menuType.value = res.data.type
    }
})
</script>
